<script setup>
import { ref, computed, onMounted } from 'vue'
import Select from 'primevue/select'
import AppDialog from '@/components/ui/AppDialog.vue'
import api from '@/api'
import { useUserStore } from '@/stores/user'
import { useNotify } from '@/services/useNotify'
import { useI18n } from '@/i18n'
import { useConfirmDelete } from '@/composables/useConfirmDelete'

const user = useUserStore()
const notify = useNotify()
const { t } = useI18n()
const { confirmDelete } = useConfirmDelete()

const extensions = ref([])
const loading = ref(false)
const total = ref(0)
const groups = ref([])

const showDialog = ref(false)
const isAdding = ref(false)
const saving = ref(false)
const editing = ref({ id: null, extension_name: '', hacker_group_id: null })

const columns = [
  { field: 'extension_id',      header: t('hackwatch.extension.colId'),        sortable: true,  default: false },
  { field: 'extension_name',    header: t('hackwatch.extension.colExtension'),  sortable: true,  default: true  },
  { field: 'hacker_group_name', header: t('hackwatch.extension.colGroup'),      sortable: true,  default: true  },
]

const resolveGroupIdFromRow = (row) => {
  if (row.hacker_group_id) return row.hacker_group_id
  if (!row.hacker_group_name) return null
  return groups.value.find(g => g.name === row.hacker_group_name)?.id ?? null
}

const fetchGroups = async () => {
  try {
    const { data } = await api.getAllHackerGroups()
    groups.value = (data || []).map(g => ({ id: g.id, name: g.name }))
  } catch (e) {
    notify.error(t('common.error'), e.response?.data?.message || t('hackwatch.extension.groupsLoadFailed'))
  }
}

const fetchExtensions = async (q = {}) => {
  loading.value = true
  try {
    const params = { limit: q.limit ?? 25, page: q.page ?? 1 }
    const res = await api.getRansomwareExtensionsMapping(params)
    if (Array.isArray(res.data)) {
      extensions.value = res.data
      total.value = res.data.length
    } else if (res.data?.extensions) {
      extensions.value = res.data.extensions
      total.value = res.data.total ?? extensions.value.length
    } else {
      extensions.value = res.data || []
      total.value = extensions.value.length
    }
  } catch (e) {
    notify.error(t('common.error'), e.response?.data?.message || t('hackwatch.extension.loadFailed'))
  } finally {
    loading.value = false
  }
}

onMounted(() => Promise.all([fetchGroups(), fetchExtensions()]))

function onQueryChange(q) { fetchExtensions(q) }

const openCreate = () => {
  isAdding.value = true
  editing.value = { id: null, extension_name: '', hacker_group_id: null }
  showDialog.value = true
}
const openEdit = (row) => {
  isAdding.value = false
  editing.value = {
    id: row.extension_id ?? row.id ?? null,
    extension_name: row.extension_name || row.name || '',
    hacker_group_id: resolveGroupIdFromRow(row)
  }
  showDialog.value = true
}

const save = async () => {
  const { id, ...payload } = editing.value
  if (!payload.extension_name?.trim()) { notify.warn(t('common.warning'), t('hackwatch.extension.nameMissing')); return }
  if (!payload.hacker_group_id) { notify.warn(t('common.warning'), t('hackwatch.extension.groupMissing')); return }
  saving.value = true
  try {
    if (isAdding.value) {
      await api.createRansomwareExtensionMapping(payload)
      notify.success(t('hackwatch.extension.saved'), t('hackwatch.extension.extensionCreated'))
    } else {
      await api.updateRansomwareExtensionMapping(id, payload)
      notify.success(t('hackwatch.extension.saved'), t('hackwatch.extension.extensionUpdated'))
    }
    showDialog.value = false
    await fetchExtensions()
  } catch (e) {
    notify.error(t('hackwatch.extension.saveFailed'), e.response?.data?.message || e.message)
  } finally {
    saving.value = false
  }
}

const removeRows = async (rows) => {
  if (!await confirmDelete(t('hackwatch.extension.confirmDelete'))) return
  try {
    await Promise.all(rows.map(r => {
      const id = r.extension_id ?? r.id
      return id ? api.deleteRansomwareExtensionMapping(id) : Promise.resolve()
    }))
    notify.success(t('hackwatch.extension.deleted'), t('hackwatch.extension.extensionDeleted'))
    await fetchExtensions()
  } catch (e) {
    notify.error(t('hackwatch.extension.deleteFailed'), e.response?.data?.message || e.message)
  }
}

const tableActions = computed(() => [
  user.can('hackwatch_extensions.change') && {
    label:          t('hackwatch.extension.editTooltip'),
    icon:           'pi pi-pencil',
    requiresSingle: true,
    command:        (rows) => openEdit(rows[0]),
  },
  user.can('hackwatch_extensions.delete') && {
    label:     t('hackwatch.extension.deleteTooltip'),
    icon:      'pi pi-trash',
    severity:  'danger',
    separator: true,
    command:   removeRows,
  },
].filter(Boolean))
</script>

<template>
  <div>
    <AppTable
      table-key="hackwatch-extensions"
      :columns="columns"
      :data="extensions"
      :total="total"
      :loading="loading"
      lazy
      :actions="tableActions"
      @refresh="fetchExtensions()"
      @query-change="onQueryChange"
    >
      <template #toolbar>
        <pButton
          v-if="user.can('hackwatch_extensions.create')"
          icon="pi pi-plus"
          :label="t('hackwatch.extension.addBtn')"
          size="small"
          @click="openCreate"
        />
      </template>

      <template #cell-hacker_group_name="{ row }">
        {{ row.hacker_group_name || groups.find(g => g.id === row.hacker_group_id)?.name || '—' }}
      </template>
    </AppTable>

    <AppDialog
      v-model="showDialog"
      :title="isAdding ? t('hackwatch.extension.dialogCreate') : t('hackwatch.extension.dialogEdit')"
      size="md"
      :showFooter="false"
    >
      <form class="grid" @submit.prevent="save">
        <div class="col-12">
          <label class="block mb-2">{{ t('hackwatch.extension.formExtension') }}</label>
          <InputText v-model.trim="editing.extension_name" class="w-full" required />
        </div>
        <div class="col-12">
          <label class="block mb-2">{{ t('hackwatch.extension.formGroup') }}</label>
          <Select
            v-model="editing.hacker_group_id"
            :options="groups"
            optionLabel="name"
            optionValue="id"
            :placeholder="t('hackwatch.extension.formGroupPlaceholder')"
            class="w-full"
            required
          />
        </div>
        <div class="col-12 flex justify-end gap-2 mt-2">
          <pButton type="button" :label="t('common.cancel')" severity="secondary" @click="showDialog = false" />
          <pButton type="submit" :label="t('common.save')" :loading="saving" />
        </div>
      </form>
    </AppDialog>
  </div>
</template>
